export interface VoiceCallStartedBody {
  userNumber: string;
  botNumber: string;
  callSid: string;
  isInbound?: boolean;
  answeredBy?: string;
  machineDetectionDuration?: string;
  outboundQuest?: string;
}

export interface VoiceCallStartRecording {
  callSid: string;
  accountSid: string;
  authToken: string;
  answeredBy?: string;
  machineDetectionDuration?: string;
}

export interface VoiceCallStreamStatus {
  callSid: string;
  streamEvent: string;
}

export interface VoiceCallDial {
  userNumber: string;
  sendDigits?: string;
  botNumber: string;
  accountSid: string;
  authToken: string;
  outboundQuest?: string;
  amdHumanQuest?: string;
  amdMachineQuest?: string;
  amdFallbackQuest?: string;
  interruptible?: boolean;
}

/**
 * What the contact-center dialer did with an agent's "Call" (ENG-3373). `real` → both legs are
 * dialing. `simulated` → the Outbound Gate withheld the customer leg (the org's calls flag is
 * off, or outside production the number is not allowed): no leg dialed, the conversation
 * records it, `callSid` is the stand-in `SIM…` id. `blocked` → the person opted out of calls;
 * the agent may repeat the request with `consentOverride: true` ("Call anyway", decision 13).
 */
export interface AgentOutboundCallResult {
  callSid: string;
  conferenceName: string;
  userId: number;
  outcome: "real" | "simulated" | "blocked";
  /** The gate's reason when not `real` (`flag_off`, `not_listed`, `opted_out`, …). */
  reason?: string;
}

/** The longest text an agent can start a conversation with (ENG-3592): Twilio's limit for one message. */
export const AGENT_TEXT_MAX_CHARS = 1600;

/** The server's request to the bot to start an agent's 1:1 text (ENG-3592). The server has resolved the customer and the from-number. */
export interface AgentTextStartRequest {
  orgId: number;
  userId: number;
  memberId: number;
  agentName: string;
  /** One of the org's Twilio numbers; the new thread is bound to it. */
  fromNumber: string;
  /** The customer's number, E.164. */
  toNumber: string;
  content: string;
  /** The server created the customer from a typed number. */
  userWasCreated?: boolean;
}

/**
 * What the bot did with an agent's text (ENG-3592). `sent`, `simulated` (the Outbound Gate held it back)
 * and `blocked` (the customer opted out) come from the delivery; `failed` is any other send result.
 * `refused` → nothing was sent because an agent already owns the customer's open text thread
 * (`reason: "active"`), an escalation on it is waiting (`reason: "pending"`), or an agent text
 * went to the customer moments ago and hasn't been taken over yet (`reason: "recent_agent_text"`).
 */
export interface AgentTextStartResult {
  outcome: "sent" | "simulated" | "blocked" | "failed" | "refused";
  /** The gate's reason or the delivery's error code (a code, never the carrier's message); for `refused`, why (see above). */
  reason?: string;
  /** The thread the text went into. Absent when refused. */
  botConversationId?: number;
  /** For `refused`: the agent who owns the thread, when one does. */
  activeAgentMemberId?: number | null;
}

/**
 * What New Conversation's Text did (ENG-3592), for Studio. A refused text never comes back as a
 * result: the server answers it with an error naming the agent who has the thread.
 */
export interface AgentOutboundTextResult {
  outcome: Exclude<AgentTextStartResult["outcome"], "refused">;
  /** The gate's reason or the delivery's error code (a code, never the carrier's message) when not `sent`. */
  reason?: string;
  userId: number;
  botConversationId?: number;
  /** The agent's takeover of the new thread, when it started. */
  assignmentId?: number;
  /** `false` when the text went out but the takeover didn't start; the agent can take over from the SMS tab. */
  takeoverStarted: boolean;
}

export interface VoiceCallUpdate {
  callSid: string;
  twiml: string;
  accountSid: string;
  authToken: string;
}

export interface VoiceCallGenerateAppToken {
  orgId?: number;
  memberId?: number;
  appAccountId?: string;
  appApiKey?: string;
  appApiSecret?: string;
  isPreflight?: boolean;
}

export interface VoiceCallBridgeToAgent {
  orgId: number;
  callSid: string;
  twilioToken: string;
  sessionId: number;
  escalationId: number;
}

export interface VoiceCallListenIn {
  orgId: number;
  twilioToken: string;
  sessionId: number;
  escalationId: number;
}

export type StreamTrack = "inbound_track" | "outbound_track" | "both_tracks";

export interface VoiceCallStartStream {
  callSid: string;
  accountSid: string;
  authToken: string;
  streamUrl: string;
  track: StreamTrack;
  statusCallback: string;
  statusCallbackMethod: string;
}

export interface VoiceCallUserTranscript {
  callSid: string;
  transcript: string;
}

export interface VoiceCallSwivlTranscript {
  callSid: string;
  transcript: string;
}

/**
 * One Contact Center softphone token (ENG-3656). The CC registers a Twilio
 * Device per Twilio account the org's numbers live on, because an agent leg is
 * dialed on the account that owns the call's number and a Device registered
 * elsewhere never hears it. All tokens of one tab share the same identity.
 */
export interface CCVoiceToken {
  /** The Twilio account this token registers on. */
  accountSid: string;
  /** `platform` = Swivl's main account (env credentials); `org` = the org's own account (its stored API key). */
  account: "platform" | "org";
  token: string;
  identity: string;
}
